import { Platform, View } from 'react-native';
import React, { useCallback, useEffect } from 'react';
import { Banner, Footer, KeyboardScreenContainer, Text } from '~/components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useMarisaAutoForm from '~/stores/marisaAutoForm';
import type { MarisaAutoStepsProps } from '../..';
import CarTheftSVG from '~/assets/svgs/car-theft.svg';
import CarAccidentSVG from '~/assets/svgs/car-accident.svg';
import FireSVG from '~/assets/svgs/fire.svg';
import AssistanceSVG from '~/assets/svgs/assistance.svg';
import BackupCarSVG from '~/assets/svgs/backup-car.svg';
import FloodingSVG from '~/assets/svgs/flooding.svg';
import AccidentThirdPartySVG from '~/assets/svgs/accident-third-party.svg';
import Service from './components/Service';
import useToast from '~/hooks/useToast';

const schema = yup
  .object()
  .shape({
    theft: yup.string().required(),
    accident: yup.string().required(),
    thridPartyAccident: yup.string().required(),
    fire: yup.string().required(),
    flooding: yup.string().required(),
    backupCar: yup.string().required(),
  })
  .required();

interface ServicesForm {
  theft: string;
  accident: string;
  thridPartyAccident: string;
  fire: string;
  flooding: string;
  backupCar: string;
}

const Services = ({ onContinue }: MarisaAutoStepsProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ServicesForm>({
    resolver: yupResolver(schema),
  });

  const { setServices } = useMarisaAutoForm();
  const { showError } = useToast();

  const onSubmit = useCallback(
    (data: ServicesForm) => {
      setServices(data);

      onContinue();
    },
    [setServices, onContinue]
  );

  useEffect(() => {
    if (
      errors.accident?.message ||
      errors.backupCar?.message ||
      errors.fire?.message ||
      errors.flooding?.message ||
      errors.theft?.message ||
      errors.thridPartyAccident?.message
    ) {
      showError({
        title: 'Selecione as opções nos itens para continuar',
        description: 'Escolha um grau de importância em cada item.',
      });
    }
  }, [errors, showError]);

  return (
    <KeyboardScreenContainer
      footer={
        <View
          className={`flex xl:hidden ${
            Platform.OS === 'web' ? 'py-4 px-6' : ''
          }`}
        >
          <Footer onSubmit={handleSubmit(onSubmit)} />
        </View>
      }
    >
      <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
        <Banner type="marisa-auto" />

        <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24 mb-4">
          <Text className="text-xl mb-4 text-gray-600">
            Nos conte qual a importância de cada cobertura/serviço para você?
          </Text>

          <Text className="text-gray-400 text-sm mb-8">
            A partir de suas respostas nosso corretor virtual vai encontrar e
            organizar os planos que possuem maior relevância para atender suas
            necessidades.
          </Text>

          <View className="border py-2 px-4 rounded border-secondary-700 bg-secondary-200">
            <Text className="text-center text-[10px] text-secondary-700">
              Toque na importância desejada de cada item abaixo:
            </Text>
          </View>

          <Service
            name="theft"
            description="Seu carro foi roubado ou furtado? Não dá pra ficar no prejuízo, né. Pra garantir que tudo fique numa boa, você poderá acionar esta cobertura para te auxiliar neste momento."
            control={control}
            icon={CarTheftSVG}
            title="Roubo e Furto"
          />

          <Service
            name="accident"
            description="Se rolar um acidente e os passageiros do veículo se ferirem ou se alguém falecer, você garante a indenização contratada, tanto pela morte quanto por uma possível invalidez permanente, parcial ou total, dos passageiros que estiverem no carro."
            control={control}
            icon={CarAccidentSVG}
            title="Acidente"
          />

          <Service
            name="thridPartyAccident"
            description="Se você estiver dirigindo e bater no carro ou bens de outras pessoas, dá pra contar com a ajuda dessa cobertura pra bancar as despesas que tiver com reparos."
            control={control}
            icon={AccidentThirdPartySVG}
            title="Danos a Terceiros"
          />

          <Service
            name="fire"
            description="Pra você e seu bolso ficarem numa boa, é melhor considerar essa cobertura na hora de montar seu seguro online. Com ela, você garante a indenização integral se um acidente com fogo ou incêndio detonar seu carro."
            control={control}
            icon={FireSVG}
            title="Incêndio"
          />

          <Service
            name="flooding"
            description="Se for pego de surpresa no meio de um alagamento ou se outros eventos naturais detonarem seu carro, essa cobertura irá te salvar."
            control={control}
            icon={FloodingSVG}
            title="Alagamento"
          />

          <Service
            name="assistance"
            description="Use as assistências quando precisar. Elas não afetam a sua franquia."
            control={control}
            icon={AssistanceSVG}
            title="Assistência"
          />

          <Service
            name="backupCar"
            description="Se alguém roubar ou furtar o seu veículo ou se um incêndio, alagamento ou batida te deixar fora da estrada, você garante um carro extra enquanto o seu fica no conserto ou o pagamento do seguro não sai."
            control={control}
            icon={BackupCarSVG}
            title="Carro Reserva"
          />

          <Footer className="hidden" onSubmit={handleSubmit(onSubmit)} />
        </View>
      </View>
    </KeyboardScreenContainer>
  );
};

export default Services;
